import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import type * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

export const AsyncSubdomainSelect = ({
  onChange,
  value,
  required,
}: FieldExtensionComponentProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [subdomains, setSubdomains] = useState<string[]>([]);
  const [selectedSubdomain, setSelectedSubdomain] = useState<string>(value || '');
  const [error, setError] = useState<string | null>(null);

  const fetchSubdomains = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('edm-connector');
      const response = await fetchApi.fetch(`${baseUrl}/subdomain`);

      if (!response.ok) {
        const errorText = await response.text();
        const statusTextSafe = (response.statusText || '').trim().replace(/\s+/g, ' ').slice(0, 100);
        const errorTextSafe = (errorText || '').trim().replace(/\s+/g, ' ').slice(0, 200);
        throw new Error(`Failed to fetch subdomains: ${statusTextSafe}${errorTextSafe ? `. ${errorTextSafe}` : ''}`);
      }

      const subdomainList = await response.json();
      setSubdomains(Array.isArray(subdomainList) ? subdomainList : []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch subdomains.');
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    fetchSubdomains();
  }, [fetchSubdomains]);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newSubdomain = event.target.value as string;
    setSelectedSubdomain(newSubdomain);
    onChange?.(newSubdomain);
  };

  return (
    <FormControl margin="normal" required={required} error={!!error} fullWidth>
      <InputLabel id="subdomain-select-label">Subdomain</InputLabel>
      <Select labelId="subdomain-select-label" value={selectedSubdomain} onChange={handleChange}>
        {subdomains.map((subdomain) => (
          <MenuItem key={subdomain} value={subdomain}>
            {subdomain}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText error>{error}</FormHelperText>}
      {!error && <FormHelperText>Select a subdomain from the list.</FormHelperText>}
    </FormControl>
  );
};